/**
 * ACE App — dark-themed mobile PWA shell. Lives at /app
 *
 * Coexists with /admin (Swiss desktop) and /command-center.
 * Phase 1: Shell + Home (Pulse metrics + Tool Grid + Next Up)
 * Phase 2 (next): Dashboard / Ops Log / Fleet / Quote / ROI page components
 *
 * Auth: relies on the global fetch interceptor (Bearer header from /m unlock).
 * Mirrors the visual look of /artifacts/9msufmw0_ACE_App.html.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast, Toaster } from 'sonner';
import { MobileClientList, MobileClientDetail } from './ace-app/MobileClients';
import { MobileDashboard, MobileOpsLog, MobileFleet, MobileQuote, MobileROI } from './ace-app/MobileTools';
import { MobileBookings } from './ace-app/MobileBookings';
import { MobileInbox } from './ace-app/MobileInbox';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Theme tokens — light Swiss UI (matches public yourautoconcierge.com) ──
const T = {
  black: '#FFFFFF',  // primary background (was the inverted "black")
  dark:  '#FAFAFA',  // input / subtle surface
  panel: '#FFFFFF',  // panel surfaces
  card:  '#FFFFFF',  // card surfaces
  border: '#E5E5E5',
  red:   '#CC0000',
  gold:  '#A8862F',  // darkened for white-bg readability
  green: '#15803D',  // darkened for white-bg readability
  amber: '#B45309',
  white: '#0A0A0A',  // primary text (semantic name preserved)
  light: '#525252',  // secondary text
  dim:   '#A3A3A3',  // tertiary / muted text
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmt$ = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;

// ── Nav items ──
const NAV = [
  { id: 'home',      label: 'Home',      icon: '⌂' },
  { id: 'clients',   label: 'Clients',   icon: '♣' },
  { id: 'ops',       label: 'Ops',       icon: '📋' },
  { id: 'fleet',     label: 'Fleet',     icon: '🚗' },
  { id: 'quote',     label: 'Quote',     icon: '♦' },
  { id: 'more',      label: 'More',      icon: '⋯' },
];

const TOOLS = [
  { id: 'inbox',     label: 'Inquiry Inbox',      desc: 'SMS · web · pipeline',       icon: '📥', accent: T.red },
  { id: 'clients',   label: 'Clients',            desc: 'CRM · profiles · history',   icon: '♣', accent: T.red },
  { id: 'bookings',  label: 'Bookings',           desc: 'Edit · delete · restore',    icon: '📅', accent: T.gold },
  { id: 'dashboard', label: 'Command Dashboard', desc: '7-Pillar P&L · KPIs',         icon: '♠', accent: T.red },
  { id: 'ops',       label: 'Ops Log v3',         desc: 'Daily P&L · members · A/R', icon: '📋', accent: T.red },
  { id: 'fleet',     label: 'Fleet Manager',      desc: 'Vehicles · pricing',         icon: '🚗', accent: T.red },
  { id: 'quote',     label: 'Quote Builder',      desc: 'Quick quote calculator',     icon: '♦', accent: T.red },
  { id: 'roi',       label: 'Membership ROI',     desc: 'Conversion math',            icon: '📝', accent: T.gold },
  { external: '/staff', label: 'Staff Portal',    desc: 'Today\u2019s ops mobile',    icon: '👷', accent: T.green },
];

export default function ACEApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const page = params.get('p') || 'home';
  const clientId = params.get('client');
  const [clock, setClock] = useState('');

  useEffect(() => {
    const tick = () => setClock(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);

  const go = (pageId, extra = '') => navigate(`/app?p=${pageId}${extra}`);
  const openClient = (key) => navigate(`/app?p=clients&client=${encodeURIComponent(key)}`);

  return (
    <div style={{ background: T.dark, color: T.white, minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "DM Sans", "Segoe UI", sans-serif' }} data-testid="ace-app">
      <Toaster position="top-center" theme="light" />

      {/* ─── Header (sticky) ─── */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 30,
        background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${T.border}`,
        padding: 'max(env(safe-area-inset-top), 12px) 16px 12px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <button onClick={() => go('home')} style={{ background: 'none', border: 0, color: T.white, display: 'flex', alignItems: 'center', gap: 8, padding: 0 }}>
          <span style={{ color: T.red, fontSize: 20, fontWeight: 900 }}>♠</span>
          <div style={{ textAlign: 'left' }}>
            <p style={{ fontSize: 13, fontWeight: 900, letterSpacing: '-0.01em', margin: 0 }}>ACE</p>
            <p style={{ fontSize: 9, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em', margin: 0 }}>Auto Concierge</p>
          </div>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: 'DM Mono, ui-monospace, monospace', fontSize: 12, color: T.light, fontVariantNumeric: 'tabular-nums' }}>{clock}</span>
          <button onClick={() => go('quote')} style={{ background: T.red, color: '#FFFFFF', border: 0, padding: '7px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer' }} data-testid="header-add-quote">
            + Quote
          </button>
        </div>
      </header>

      {/* ─── Body ─── */}
      <main style={{ padding: '20px 16px 100px', maxWidth: 480, margin: '0 auto' }} data-testid={`page-${page}`}>
        {page === 'home' && <HomePage onTool={(t) => t.external ? window.open(t.external, '_blank') : go(t.id)} />}
        {page === 'clients' && (clientId
          ? <MobileClientDetail clientKey={clientId} onBack={() => go('clients')} />
          : <MobileClientList onOpen={openClient} />
        )}
        {page === 'bookings' && <MobileBookings onOpenClient={openClient} />}
        {page === 'inbox' && <MobileInbox onOpenClient={openClient} />}
        {page === 'dashboard' && <MobileDashboard />}
        {page === 'ops' && <MobileOpsLog />}
        {page === 'fleet' && <MobileFleet />}
        {page === 'quote' && <MobileQuote />}
        {page === 'roi' && <MobileROI />}
        {page === 'more' && <MorePage onTool={(t) => t.external ? window.open(t.external, '_blank') : go(t.id)} />}
      </main>

      {/* ─── Bottom Nav ─── */}
      <nav style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 30,
        background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(20px)',
        borderTop: `1px solid ${T.border}`,
        display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)',
        padding: `8px 0 max(env(safe-area-inset-bottom), 10px)`,
      }} data-testid="bottom-nav">
        {NAV.map(n => {
          const active = page === n.id;
          return (
            <button
              key={n.id}
              onClick={() => go(n.id)}
              style={{
                background: 'none', border: 0, padding: '6px 0',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                color: active ? T.red : T.dim, cursor: 'pointer',
              }}
              data-testid={`nav-${n.id}`}
            >
              <span style={{ fontSize: 18 }}>{n.icon}</span>
              <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{n.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

// ─── Home Page ───
function HomePage({ onTool }) {
  const [pulse, setPulse] = useState({ today: 0, week: 0, mrr: 0, ar_due: 0, bookings_today: 0 });
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const today = todayISO();
        const weekStart = new Date(); weekStart.setDate(weekStart.getDate() - 6);
        const dates = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date(); d.setDate(d.getDate() - i);
          dates.push(d.toISOString().slice(0, 10));
        }
        const [todayPL, ...weekPL] = await Promise.all([
          fetch(`${API}/api/command-center/ops/daily-pl?date=${today}`).then(r => r.json()).catch(() => ({})),
          ...dates.map(d => fetch(`${API}/api/command-center/ops/daily-pl?date=${d}`).then(r => r.json()).catch(() => ({}))),
        ]);
        const members = await fetch(`${API}/api/command-center/ops/members`).then(r => r.json()).catch(() => ({ members: [] }));
        const settings = await fetch(`${API}/api/command-center/ops/settings`).then(r => r.json()).catch(() => ({}));
        const bookings = await fetch(`${API}/api/command-center/confirmed-bookings`).then(r => r.json()).catch(() => ({ bookings: [] }));

        if (cancelled) return;
        // MRR estimate = sum of monthly_retainer across active tiered members
        const mrr = (members.members || []).reduce((s, m) => s + (m.tier_meta?.monthly_retainer || 0), 0);
        const ar_due = settings?.current_ar_balance || 0;
        const week = weekPL.reduce((s, d) => s + (d.gross || 0), 0);
        const upcomingList = (bookings.bookings || [])
          .filter(b => b.event_date >= today && (b.cc_status || 'upcoming') === 'upcoming')
          .sort((a, b) => a.event_date.localeCompare(b.event_date))
          .slice(0, 5);

        setPulse({
          today: todayPL.gross || 0,
          week,
          mrr,
          ar_due,
          bookings_today: todayPL.bookings_count || 0,
        });
        setUpcoming(upcomingList);
      } catch (e) { toast.error('Failed to load pulse data'); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div>
      {/* Hero / branding */}
      <section style={{
        background: `radial-gradient(circle at 30% 0%, ${T.red}20, transparent 60%), ${T.panel}`,
        border: `1px solid ${T.border}`,
        padding: '24px 20px', marginBottom: 16,
      }}>
        <p style={{ fontSize: 9, color: T.red, textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: 0 }}>Your Auto Concierge</p>
        <h1 style={{ fontSize: 30, fontWeight: 900, margin: '8px 0 4px', letterSpacing: '-0.02em' }}>Welcome back.</h1>
        <p style={{ fontSize: 13, color: T.light, margin: 0 }}>{pulse.bookings_today} bookings today · pulse below</p>
      </section>

      {/* Pulse */}
      <p style={{ fontSize: 9, color: T.red, textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: '0 4px 10px' }}>Pulse</p>
      <section style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 24 }} data-testid="pulse-grid">
        <PulseCard label="Today" value={loading ? '…' : fmt$(pulse.today)} accent={T.green} testid="pulse-today" />
        <PulseCard label="Week" value={loading ? '…' : fmt$(pulse.week)} accent={T.amber} testid="pulse-week" />
        <PulseCard label="MRR" value={loading ? '…' : fmt$(pulse.mrr)} accent={T.gold} testid="pulse-mrr" />
        <PulseCard label="A/R Due" value={loading ? '…' : fmt$(pulse.ar_due)} accent={T.red} testid="pulse-ar" />
      </section>

      {/* Tool Grid */}
      <p style={{ fontSize: 9, color: T.red, textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: '0 4px 10px' }}>Tools</p>
      <section style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 24 }} data-testid="tool-grid">
        {TOOLS.map((t, i) => (
          <button
            key={t.id || t.external}
            onClick={() => onTool(t)}
            style={{
              background: T.card, border: `1px solid ${T.border}`,
              padding: 14, textAlign: 'left', cursor: 'pointer',
              color: T.white, transition: 'border-color 0.15s',
            }}
            onMouseDown={e => e.currentTarget.style.borderColor = t.accent}
            onMouseUp={e => e.currentTarget.style.borderColor = T.border}
            data-testid={`tool-${t.id || 'external'}-${i}`}
          >
            <span style={{ fontSize: 22, color: t.accent }}>{t.icon}</span>
            <p style={{ fontSize: 13, fontWeight: 800, margin: '6px 0 2px' }}>{t.label}</p>
            <p style={{ fontSize: 10, color: T.light, margin: 0, lineHeight: 1.3 }}>{t.desc}</p>
          </button>
        ))}
      </section>

      {/* Next Up */}
      <p style={{ fontSize: 9, color: T.red, textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: '0 4px 10px' }}>Next Up</p>
      <section data-testid="next-up">
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: T.dim, fontSize: 12 }}>Loading…</div>
        ) : upcoming.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: T.dim, fontSize: 12, background: T.card, border: `1px solid ${T.border}` }}>
            No upcoming bookings.
          </div>
        ) : upcoming.map(b => (
          <div key={b.booking_id} style={{ background: T.card, border: `1px solid ${T.border}`, padding: 12, marginBottom: 6 }} data-testid={`upcoming-${b.booking_id}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 800, margin: 0 }}>{b.client_name || '—'}</p>
                <p style={{ fontSize: 11, color: T.light, margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.vehicle_name || b.vehicle || '—'}</p>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <p style={{ fontSize: 11, color: T.red, fontFamily: 'DM Mono, monospace', margin: 0, fontWeight: 700 }}>{b.event_date}</p>
                <p style={{ fontSize: 10, color: T.light, margin: 0 }}>{b.pickup_time || ''}</p>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Unlock prompt if no session */}
      <div style={{ marginTop: 32, padding: 12, border: `1px dashed ${T.border}`, textAlign: 'center' }}>
        <p style={{ fontSize: 10, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em', margin: '0 0 8px' }}>Need access?</p>
        <button
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + '/app';
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          style={{ display: 'block', width: '100%', padding: '10px', background: '#0A0A0A', color: '#FFFFFF', border: 0, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer', marginBottom: 8 }}
          data-testid="app-google-signin"
        >
          Sign in with Google
        </button>
        <Link to="/m" style={{ fontSize: 11, color: T.red, textDecoration: 'underline', fontWeight: 700 }}>Unlock with passcode →</Link>
      </div>
    </div>
  );
}

const PulseCard = ({ label, value, accent, testid }) => (
  <div style={{ background: T.card, border: `1px solid ${T.border}`, padding: '12px 14px' }} data-testid={testid}>
    <p style={{ fontSize: 9, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 700, margin: 0 }}>{label}</p>
    <p style={{ fontSize: 22, fontWeight: 900, color: accent, margin: '4px 0 0', fontFamily: 'DM Mono, monospace', fontVariantNumeric: 'tabular-nums' }}>{value}</p>
  </div>
);

const PlaceholderPage = ({ title, subtitle }) => (
  <div style={{ padding: '40px 20px', textAlign: 'center' }} data-testid="placeholder-page">
    <p style={{ fontSize: 9, color: T.red, textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: 0 }}>Phase 3</p>
    <h2 style={{ fontSize: 24, fontWeight: 900, margin: '8px 0 4px' }}>{title}</h2>
    <p style={{ fontSize: 12, color: T.light, margin: 0 }}>{subtitle}</p>
    <Link to="/app?p=home" style={{ display: 'inline-block', marginTop: 20, padding: '10px 20px', background: T.red, color: '#FFFFFF', textDecoration: 'none', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>← Back to Home</Link>
  </div>
);

const MorePage = ({ onTool }) => (
  <div data-testid="more-page">
    <p style={{ fontSize: 9, color: T.red, textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: '0 4px 10px' }}>All Tools</p>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      {TOOLS.map((t, i) => (
        <button
          key={t.id || t.external}
          onClick={() => onTool(t)}
          style={{
            background: T.card, border: `1px solid ${T.border}`,
            padding: 14, textAlign: 'left', cursor: 'pointer', color: T.white,
          }}
          data-testid={`more-tool-${t.id || 'external'}-${i}`}
        >
          <span style={{ fontSize: 22, color: t.accent }}>{t.icon}</span>
          <p style={{ fontSize: 13, fontWeight: 800, margin: '6px 0 2px' }}>{t.label}</p>
          <p style={{ fontSize: 10, color: T.light, margin: 0, lineHeight: 1.3 }}>{t.desc}</p>
        </button>
      ))}
    </div>
    <div style={{ marginTop: 20, padding: 12, border: `1px solid ${T.border}`, background: T.card }}>
      <p style={{ fontSize: 9, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em', margin: '0 0 8px' }}>Other</p>
      <Link to="/admin" style={{ display: 'block', color: T.white, fontSize: 13, fontWeight: 700, padding: '8px 0', textDecoration: 'none' }} data-testid="more-admin">Desktop Admin →</Link>
      <Link to="/m" style={{ display: 'block', color: T.white, fontSize: 13, fontWeight: 700, padding: '8px 0', textDecoration: 'none' }} data-testid="more-mobile-unlock">Mobile Unlock →</Link>
    </div>
  </div>
);
